// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Defines the canonical markdown projection used by guidance list and edit UI.
 *
 * Guidance remains stored as portable markdown. These pure helpers derive its
 * title, issue, preview, and risk display while preserving sections they do not
 * own, avoiding a second component-specific data format.
 */

export interface GuidanceContent {
  title: string
  issue: string
  preview: string
  body: string
}

const SECTION = /(?:^|\n)##\s+([^\n]+)\n([\s\S]*?)(?=\n##\s+|$)/g
const ISSUE_SECTION = /(?:^|\n)##\s+Issue(?:\s*\n[\s\S]*?(?=\n##\s+|$)|\s*$)/i

function plainLine(line: string): string {
  return line
    .replace(/\[([^\]]+)]\([^)]+\)/g, '$1')
    .replace(/&#32;|&nbsp;/gi, ' ')
    .replace(/^[#>*_`\s]+|[*_`\s]+$/g, '')
    .trim()
}

export function parseGuidanceContent(content: string): GuidanceContent {
  const lines = content.trim().split('\n')
  const titleMatch = content.match(/^#\s+(.+)$/m)
  const fallbackTitleIndex = lines.findIndex(line => line.trim().length > 0)
  const title = plainLine(titleMatch?.[1] ?? lines[fallbackTitleIndex] ?? '')
  const body = titleMatch
    ? content.replace(/^#\s+.+$\n*/m, '').trim()
    : lines.slice(fallbackTitleIndex + 1).join('\n').trim()
  const sections = new Map<string, string>()

  for (const match of body.matchAll(SECTION)) {
    sections.set(match[1].trim().toLowerCase(), match[2].trim())
  }

  const preview = body
    .split('\n')
    .map(line => ({ markdown: line.trim(), text: plainLine(line) }))
    .find(line =>
      line.text.length > 0
      && !/^#{1,6}\s/.test(line.markdown)
      && !(line.text.length < 80 && line.text.endsWith(':')),
    )?.text ?? ''

  return {
    title,
    issue: sections.get('issue') ?? '',
    preview,
    body,
  }
}

export function updateGuidanceContent(content: string, title: string, issue: string): string {
  const nextTitle = title.trim()
  const nextIssue = issue.trim()
  const remainingBody = parseGuidanceContent(content).body.replace(ISSUE_SECTION, '').trim()
  return `# ${nextTitle}\n\n## Issue\n${nextIssue}${remainingBody ? `\n\n${remainingBody}` : '\n'}`
}

export type RiskLevel = 'low' | 'medium' | 'high'

export function riskLevel(score: number | null | undefined): RiskLevel {
  if ((score ?? 0) >= 4) return 'high'
  if ((score ?? 0) >= 3) return 'medium'
  return 'low'
}

export const RISK_SCORE: Record<RiskLevel, number> = { low: 2, medium: 3, high: 5 }
